/**
 * What the guide knows about each screen (Captain Bram, guide.ts): the plain-value facts the level, hub and end screen
 * build for it. Types and one constant only, no imports, so node tests can use it.
 */

export type LevelPhaseName = "draft" | "ready" | "wave" | "break" | "won" | "lost";
export type OfferName = "openingRoad" | "openingTower" | "inRun";
/** The kinds of card a hand holds; "ascend" and "skill" are the tier-4 special cards (docs/TOWERS_V2.md). */
export type CardKindName = "road" | "tower" | "upgrade" | "ascend" | "skill" | "move";

/**
 * The level as the guide sees it: plain values built by WP-B (level/guideLevel.ts levelFacts) after every sim change.
 * Never a live sim reference: scripts stay pure and testable.
 */
export interface LevelFacts {
  readonly level: number;
  /** Level not yet cleared on this save. */
  readonly firstClear: boolean;
  readonly phase: LevelPhaseName;
  readonly stage: number;
  readonly stages: number;
  /** Sim seconds since the current wave started; 0 outside a wave. */
  readonly waveSeconds: number;
  readonly offer: OfferName | null;
  readonly offerCards: readonly CardKindName[];
  /** Tower ids of the tower cards in the open offer, in card order. */
  readonly offerTowerIds: readonly string[];
  readonly picked: CardKindName | null;
  /** The picked tower card's tower id (TOWERS_V2 hints: the Mortar's blind ring), null without a tower card picked. */
  readonly pickedTowerId?: string | null;
  /** Road options for the picked road card (0 otherwise). */
  readonly fits: number;
  readonly towerSpotChosen: boolean;
  readonly upgradeTargetChosen: boolean;
  /** Road tiles laid. The road never finishes (DESIGN §4.3): more road cards come through the level, further apart. */
  readonly roadTiles: number;
  /** Towers standing, the base excluded. */
  readonly towersBuilt: number;
  readonly rerollCharges: number;
  readonly speed: number;
  readonly points: number;
  readonly pointsToNextOffer: number;
  readonly bossAlive: boolean;
  /** Distinct archetype ids of living enemies. */
  readonly aliveEnemyIds: readonly string[];
  readonly airEnemyAlive: boolean;
  /** An elite (an enemy with an affix: Warded, Fireproof…) is alive. */
  readonly eliteAlive: boolean;
  /** Troops are fighting an enemy right now. */
  readonly troopsFighting: boolean;
  readonly lastStand: boolean;
  /** Names of the towers owned on this save that can hit flyers, base excluded ("Archer", "Frost", …). */
  readonly airTowerNames: readonly string[];
  /** This level's third star forbids SKIP. */
  readonly noSkipChallenge: boolean;
  readonly towerSheetOpen: boolean;
  readonly rallyArmed: boolean;
  /** Pets v2: a wild pup is out on the grass to be tapped (domain canEngagePet). */
  readonly wildPetOut: boolean;
}

/** The hub as the guide sees it, built by WP-C (ui/hub/hubView.ts) on every render and each 1 s tick. */
export interface HubFacts {
  readonly highestCleared: number;
  readonly nextLevel: number;
  /** Something is equipped. */
  readonly equipped: boolean;
  /** A tower or the fortress is upgrading or above level 1. */
  readonly upgraded: boolean;
  /** The builder chip's suggestion: what to upgrade and how long it takes. */
  readonly builderSuggestion: { readonly name: string; readonly seconds: number } | null;
  /** The next level needs gear and readiness says the player is under-geared. */
  readonly gearRequired: boolean;
  readonly gearRequiredFrom: number;
  /** Gear items in the stash (not equipped). */
  readonly stashCount: number;
  /** Owned towers not yet looked at in the Towers page (the NEW badge). */
  readonly newTowerIds: readonly string[];
  readonly ordersUnlocked: boolean;
  /** The open sheet ("gear", "towers", "upgrade", "more", "guide", "checkin", "campaign", "commander", …) or null. */
  readonly sheet: string | null;
  /** What the features added in waves 1–3 show (ui/hub/hubGuide.ts `setHubExtras`); NO_HUB_EXTRAS before they register. */
  readonly extras: HubExtras;
}

/**
 * The hub's feature facts for the guide's just-in-time hints (FINISH_PLAN WP9): built by features/guide.ts from the
 * catalog and the feature registry, so the hub view stays frozen. Plain values only.
 */
export interface HubExtras {
  /** The ids of the More rows on show (features/registry.ts MoreRow ids: "events", "market", "achievements"…). */
  readonly moreRows: readonly string[];
  /** Each shown More row's red count (a row without one is absent): "achievements" → rewards to claim. */
  readonly moreDots: Readonly<Record<string, number>>;
  /** The event running now, or null. */
  /** The live event; `how` is its way to earn this week in a few words (MP-21: "Beat boss levels and win on Heroic"). */
  readonly event: { readonly name: string; readonly currency: string; readonly how?: string } | null;
  /** The Weekly Trial is open for this player's progress. */
  readonly trialOpen: boolean;
  /** A cleared level has its Heroic tier open and not yet won. */
  readonly heroicOpen: boolean;
  /** Fortress Tribute fills (its unlock level is cleared). */
  readonly tributeUnlocked: boolean;
  /** Rare-or-better gear in the stash the Trading Post would buy. */
  readonly marketSellable: number;
  /** M30: Marks held (the Marks pill's first line waits for the first Marks or something to sell). */
  readonly marks?: number;
  /** M21: some level's Iron challenge is open (a 3-star level). */
  readonly ironOpen: boolean;
  /** M21: Endless is unlocked. */
  readonly endlessOpen: boolean;
  /** M21: last week's Endless chest waits to be claimed. */
  readonly endlessChest: boolean;
  /** MP-20: Daily Road is open (absent before the feature registers). */
  readonly dailyOpen?: boolean;
  /** MP-27: the Hall of Records has its first record. */
  readonly recordsAny?: boolean;
  /** MP-28: challenge codes are open. */
  readonly challengeOpen?: boolean;
  /** MP-23: Trophies held (Captain Bram's first line about the Trophy Hall waits for the first ones). */
  readonly trophies?: number;
  /** M22 gear v2: the forge as Bram sees it (absent before the forge feature registers). */
  readonly forge?: ForgeFacts;
  /** M35: Gold Research nodes open now, and how many the gold held covers (@highmarch/app researchSummary). */
  readonly research?: { readonly available: number; readonly affordable: number };
}

/** Gear v2's forge facts for Captain Bram's just-in-time lines (features/guide.ts). */
export interface ForgeFacts {
  /** Forge Essence held. */
  readonly essence: number;
  /** A worn item can be raised a level with the essence held. */
  readonly raisable: boolean;
  /** Rarity × kind groups the stash could fuse now (the FUSE badge). */
  readonly fusable: number;
  /** Free-route milestones reached and not claimed (the Free rewards badge). */
  readonly freeRewards: number;
}
export const NO_HUB_EXTRAS: HubExtras = {
  moreRows: [], moreDots: {}, event: null, trialOpen: false, heroicOpen: false, tributeUnlocked: false, marketSellable: 0, ironOpen: false, endlessOpen: false, endlessChest: false,
};

/** The level-end screen. */
export interface EndFacts {
  readonly level: number;
  readonly won: boolean;
  readonly chestOpened: boolean;
  /** This win is the save's first clear of any level. */
  readonly firstWin: boolean;
  /** This loss is the save's first loss. */
  readonly firstLoss: boolean;
}
